import type { AxiosInstance, AxiosResponse } from "axios";
import type {
    ApiEnvelope,
    SdwanBranchDetailsResponse,
    SdwanDhcpResponse,
    SdwanHubDetailsResponse,
    SdwanRoutesResponse,
} from "../sdwan.types";

/**
 * GET /kontroledge/sdwan/:id — Hub detail (Nivel 2).
 * FortiManager portions are live (one multi-target call covers all branches).
 */
export function fetchSdwanHubDetails(
    api: AxiosInstance,
    tenantId: number,
    sdwanId: string | number
): Promise<AxiosResponse<ApiEnvelope<SdwanHubDetailsResponse>>> {
    return api.get(`/kontroledge/sdwan/${sdwanId}`, {
        params: { tenantId },
    });
}

/**
 * GET /kontroledge/sdwan/:id/branches/:branchRef — Branch detail (Nivel 3).
 * branchRef is the catalog auto-increment id (preferred, stable) or a device
 * name (legacy fallback). Live from FortiManager; scoped to the SD-WAN service.
 */
export function fetchSdwanBranchDetails(
    api: AxiosInstance,
    tenantId: number,
    sdwanId: string | number,
    branchRef: string | number
): Promise<AxiosResponse<ApiEnvelope<SdwanBranchDetailsResponse>>> {
    return api.get(
        `/kontroledge/sdwan/${sdwanId}/branches/${encodeURIComponent(String(branchRef))}`,
        { params: { tenantId } }
    );
}

/**
 * GET /kontroledge/sdwan/:id/branches/:branchRef/routes — branch routing table
 * (RIB) live from FortiManager. Only the routes; BGP-learned routes show
 * type 'bgp'.
 */
export function fetchSdwanBranchRoutes(
    api: AxiosInstance,
    tenantId: number,
    sdwanId: string | number,
    branchRef: string | number
): Promise<AxiosResponse<ApiEnvelope<SdwanRoutesResponse>>> {
    return api.get(
        `/kontroledge/sdwan/${sdwanId}/branches/${encodeURIComponent(String(branchRef))}/routes`,
        { params: { tenantId } }
    );
}

/**
 * GET /kontroledge/sdwan/:id/branches/:branchRef/dhcp — live client-facing DHCP
 * summary (connected devices, network capacity and utilization, per-VLAN
 * breakdown) from FortiManager.
 */
export function fetchSdwanBranchDhcp(
    api: AxiosInstance,
    tenantId: number,
    sdwanId: string | number,
    branchRef: string | number
): Promise<AxiosResponse<ApiEnvelope<SdwanDhcpResponse>>> {
    return api.get(
        `/kontroledge/sdwan/${sdwanId}/branches/${encodeURIComponent(String(branchRef))}/dhcp`,
        { params: { tenantId } }
    );
}
